import React from 'react';
import { Document, Page, Text, View, StyleSheet, pdf, Image } from '@react-pdf/renderer';

// Estilos para el PDF
const styles = StyleSheet.create({
    page: {
        flexDirection: 'column',
        backgroundColor: '#FFFFFF',
        padding: 25,
        fontFamily: 'Helvetica',
    },
    header: {
        marginBottom: 15,
        textAlign: 'center',
        borderBottomWidth: 2,
        borderBottomStyle: 'solid',
        borderBottomColor: '#000000',
        paddingBottom: 8,
    },
    title: {
        fontSize: 24,
        fontWeight: 'bold',
        marginBottom: 5,
    },
    subtitle: {
        fontSize: 14,
        color: '#666666',
    },
    section: {
        margin: 5,
        padding: 8,
    },
    row: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 5,
    },
    label: {
        fontSize: 12,
        fontWeight: 'bold',
        color: '#333333',
    },
    value: {
        fontSize: 12,
        color: '#000000',
    },
    signatureSection: {
        marginTop: 15,
        padding: 15,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#CCCCCC',
    },
    signatureTitle: {
        fontSize: 14,
        fontWeight: 'bold',
        marginBottom: 10,
        textAlign: 'center',
    },
    signatureImage: {
        width: 180,
        height: 80,
        marginBottom: 8,
        alignSelf: 'center',
    },
    paymentMethodSection: {
        marginTop: 10,
        padding: 10,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#E0E0E0',
        backgroundColor: '#F8F9FA',
    },
    paymentMethodTitle: {
        fontSize: 14,
        fontWeight: 'bold',
        marginBottom: 10,
        color: '#333333',
    },
    paymentMethodText: {
        fontSize: 12,
        color: '#555555',
        marginBottom: 5,
        fontStyle: 'italic',
    },
    evidenceSection: {
        marginTop: 10,
        padding: 15,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#CCCCCC',
    },
    evidenceTitle: {
        fontSize: 14,
        fontWeight: 'bold',
        marginBottom: 10,
        textAlign: 'center',
        color: '#333333',
    },
    evidenceImage: {
        maxWidth: 350,
        maxHeight: 250,
        marginBottom: 8,
        alignSelf: 'center',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#E0E0E0',
    },
    referenceText: {
        fontSize: 11,
        color: '#666666',
        textAlign: 'center',
        marginTop: 5,
    },
    footer: {
        position: 'absolute',
        bottom: 30,
        left: 30,
        right: 30,
        textAlign: 'center',
        fontSize: 10,
        color: '#666666',
    },
});

// Componente del documento PDF
const AbonoReceiptDocument = ({ movementData, provider, signature, evidence }) => {
    const currentDate = new Date().toLocaleDateString('es-CO', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });

    const newDebt = provider.deudaRestante - movementData.amount;

    // Función para obtener el mensaje del método de pago
    const getPaymentMethodMessage = () => {
        switch (movementData.paymentMethod) {
            case 'presencial':
                return 'Abono recibido en efectivo de forma presencial';
            case 'transferencia':
                return 'Abono realizado vía transferencia bancaria';
            case 'otro':
                return 'Abono realizado mediante otro método de pago';
            default:
                return 'Abono recibido en efectivo de forma presencial';
        }
    };

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                {/* Header */}
                <View style={styles.header}>
                    <Text style={styles.title}>COMPROBANTE DE ABONO</Text>
                    <Text style={styles.subtitle}>Minymol - Sistema de Abonos Inteligentes</Text>
                </View>

                {/* Información del movimiento */}
                <View style={styles.section}>
                    <View style={styles.row}>
                        <Text style={styles.label}>Fecha y Hora:</Text>
                        <Text style={styles.value}>{currentDate}</Text>
                    </View>
                    <View style={styles.row}>
                        <Text style={styles.label}>Proveedor:</Text>
                        <Text style={styles.value}>{provider.name}</Text>
                    </View>
                    <View style={styles.row}>
                        <Text style={styles.label}>Valor del Abono:</Text>
                        <Text style={styles.value}>${movementData.amount.toLocaleString('es-CO')}</Text>
                    </View>
                    {movementData.description && (
                        <View style={styles.row}>
                            <Text style={styles.label}>Descripción:</Text>
                            <Text style={styles.value}>{movementData.description}</Text>
                        </View>
                    )}
                </View>

                {/* Método de pago */}
                <View style={styles.paymentMethodSection}>
                    <Text style={styles.paymentMethodTitle}>MÉTODO DE PAGO</Text>
                    <Text style={styles.paymentMethodText}>
                        {getPaymentMethodMessage()}
                    </Text>
                    {movementData.paymentMethod === 'transferencia' && movementData.transferReference && (
                        <View style={styles.row}>
                            <Text style={styles.label}>Referencia de Transferencia:</Text>
                            <Text style={styles.value}>{movementData.transferReference}</Text>
                        </View>
                    )}
                    {movementData.paymentMethod === 'otro' && movementData.otherMethodDescription && (
                        <View style={styles.row}>
                            <Text style={styles.label}>Descripción del Método:</Text>
                            <Text style={styles.value}>{movementData.otherMethodDescription}</Text>
                        </View>
                    )}
                </View>

                {/* Resumen de cuenta */}
                <View style={styles.section}>
                    <Text style={[styles.label, { marginBottom: 10 }]}>RESUMEN DE CUENTA:</Text>
                    <View style={styles.row}>
                        <Text style={styles.label}>Saldo Anterior:</Text>
                        <Text style={styles.value}>${provider.deudaRestante.toLocaleString('es-CO')}</Text>
                    </View>
                    <View style={styles.row}>
                        <Text style={styles.label}>Abono Realizado:</Text>
                        <Text style={styles.value}>-${movementData.amount.toLocaleString('es-CO')}</Text>
                    </View>
                    <View style={styles.row}>
                        <Text style={[styles.label, { fontSize: 14 }]}>Nuevo Saldo:</Text>
                        <Text style={[styles.value, { fontSize: 14, fontWeight: 'bold' }]}>
                            ${newDebt.toLocaleString('es-CO')}
                        </Text>
                    </View>
                    {newDebt === 0 && (
                        <Text style={[styles.value, { textAlign: 'center', marginTop: 10, fontSize: 14, fontWeight: 'bold' }]}>
                            ¡CUENTA SALDADA COMPLETAMENTE!
                        </Text>
                    )}
                </View>

                {/* Evidencia de transferencia */}
                {evidence && movementData.paymentMethod === 'transferencia' && (
                    <View style={styles.evidenceSection}>
                        <Text style={styles.evidenceTitle}>Comprobante de Transferencia</Text>
                        <Image
                            style={styles.evidenceImage}
                            src={evidence}
                        />
                        <Text style={styles.referenceText}>
                            Evidencia del comprobante de transferencia bancaria
                        </Text>
                        {movementData.transferReference && (
                            <Text style={styles.referenceText}>
                                Ref: {movementData.transferReference}
                            </Text>
                        )}
                    </View>
                )}

                {/* Firma del proveedor */}
                {signature && (
                    <View style={styles.signatureSection}>
                        <Text style={styles.signatureTitle}>Firma del Proveedor</Text>
                        <Image
                            style={styles.signatureImage}
                            src={signature}
                        />
                        <Text style={[styles.value, { textAlign: 'center' }]}>
                            {provider.name}
                        </Text>
                    </View>
                )}

                {/* Footer */}
                <Text style={styles.footer}>
                    Este documento fue generado automáticamente por Minymol
                </Text>
            </Page>
        </Document>
    );
};

/**
 * Genera y descarga el PDF del comprobante de abono
 */
export const generateAndDownloadPDF = async (movementData, provider) => {
    try {
        // Usar evidencia directa de los datos guardados, con fallback a localStorage
        let evidence = movementData.evidenceData || localStorage.getItem('abonoEvidence');
        let signature = movementData.signatureData || localStorage.getItem('abonoSignature');
        
        // Crear el documento PDF
        const doc = <AbonoReceiptDocument 
            movementData={movementData} 
            provider={provider} 
            signature={signature}
            evidence={evidence}
        />;

        // Generar el PDF como blob
        const asPdf = pdf(doc);
        const blob = await asPdf.toBlob();

        // Crear enlace de descarga
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `comprobante_abono_${provider.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
        
        // Simular click para descargar
        document.body.appendChild(link);
        link.click();
        
        // Usar setTimeout para asegurar que el click se procese antes de remover
        setTimeout(() => {
            try {
                if (link.parentNode === document.body) {
                    document.body.removeChild(link);
                }
                URL.revokeObjectURL(url);
            } catch (e) {
                console.error('Error al limpiar link de descarga:', e);
            }
        }, 100);
        
        return true;
    } catch (error) {
        console.error('Error generando PDF:', error);
        throw new Error('Error al generar el comprobante PDF');
    }
};

export default AbonoReceiptDocument;
